import { Fragment, useState } from 'react';
import { Link } from '@/i18n/routing';
import { useTranslations } from 'next-intl';
import { useSuspenseQuery } from '@apollo/client';
import { LocationListingGroupsQuery, SearchListingDetailsQuery } from '@/query';
import { getListingLocaleSlug } from '@/utils';
import { titleConcat } from '@/utils/meta';
import { ExtendedFooterTabs } from './ExtendedFooterTabs';
import { LocationListingGroups } from './LocationListingGroups';
import { TabContentGrid } from './TabContentGrid';

export const ExtendedFooterListing = ({
  defaultFooter,
  query,
  locale,
  blogData,
}) => {
  const t = useTranslations();
  const [currentTab, setTab] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);

  const { data: listingData } = useSuspenseQuery(SearchListingDetailsQuery, {
    variables: {
      slug: query.slug,
      locale,
    },
    skip: !!query.groupSlug,
    errorPolicy: 'ignore',
  });

  const { data: groupsData } = useSuspenseQuery(LocationListingGroupsQuery, {
    variables: {
      filter: {
        listingSlug: listingData?.showListing?.slug || query.slug,
      },
      locale,
      page: 1,
    },
    skip: !!query.groupSlug,
    errorPolicy: 'ignore',
  });

  const parentGroups = listingData?.showListing?.breadcrumbListingGroups || [
    groupsData?.listingGroups?.collection[0],
  ];

  const tabs = [];
  const pages = Array(currentPage).keys();
  let isEmptyGroups = false;

  if (!!groupsData?.listingGroups?.metadata?.totalCount) {
    tabs.push({
      name: t('destinations.footer.tab1'),
      slug: 'popular',
      groups: true,
    });
  } else {
    isEmptyGroups = true;
  }

  if (blogData?.blogs?.collection?.length) {
    const blog = blogData.blogs.collection[0];
    tabs.push({
      name: blog.name,
      slug: blog.slug,
      list: (blog?.posts?.collection || []).map((post) => ({
        name: post.title,
        href: `/blogs/${blog.slug}/${post.slug}`,
      })),
    });
  }

  return (
    <div className="flex flex-col w-full">
      {isEmptyGroups ? (
        <div className="max-sm:mb-20 mb-8">{defaultFooter}</div>
      ) : tabs.length ? (
        <>
          <ExtendedFooterTabs
            tabs={tabs}
            currentTab={currentTab}
            setTab={setTab}
          />
          <div className="max-sm:mb-20 my-8">
            {tabs.map((tab, index) => (
              <div
                key={tab.name}
                className={`tab-item ${
                  tabs[currentTab] && index === currentTab
                    ? 'tab-active'
                    : 'tab-hidden'
                }`}
              >
                <div className="tab-content">
                  <TabContentGrid>
                    {tab.groups
                      ? [...pages].map((page) => (
                          <LocationListingGroups
                            key={page}
                            page={page + 1}
                            slug={listingData?.showListing?.slug}
                            locale={locale}
                            currentPage={currentPage}
                            setCurrentPage={setCurrentPage}
                          />
                        ))
                      : tab.list.map((item, i) => (
                          <div key={i} className="flex flex-col gap-1 min-w-0">
                            <Link
                              href={item.href}
                              className="text-primary-700 text-lg font-normal underline truncate"
                            >
                              {item.name}
                            </Link>
                          </div>
                        ))}
                  </TabContentGrid>
                </div>
              </div>
            ))}
          </div>
        </>
      ) : null}
      <div className="flex flex-wrap gap-0.5 text-base">
        <Link href="/" className="text-primary-700 font-normal underline">
          Guestit
        </Link>
        {parentGroups?.length
          ? parentGroups.map((parentGroup, i) =>
              parentGroup ? (
                <Fragment key={i}>
                  <span className="icon-right_light size-6 text-center" />
                  <Link
                    href={`/destinations/${getListingLocaleSlug(
                      parentGroup,
                      locale
                    )}`}
                    className="text-slate-500 font-normal underline"
                  >
                    {parentGroup.name}
                  </Link>
                </Fragment>
              ) : null
            )
          : null}
        <span className="icon-right_light size-6 text-center" />
        <span className="text-slate-500 font-normal">
          {titleConcat(listingData?.showListing?.name)}
        </span>
      </div>
    </div>
  );
};
